import {
  defaultGenericElement,
  reversionElement,
  updateElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { useEditorStore } from '../../stores/useEditorStore';
import { hexToRgbString } from '../../utils/color';
import { distance, type Point } from '../../utils/math';
import { type BooleanElementGroupType } from '../boolean';
import { general } from '../general';
import { poly, type GenericPolyElement } from '../poly';

import { createFreedrawEvents } from './events';
import { getFreeDrawElementAbsoluteCoords } from './utils/coords';
import { getStrokePath } from './utils/path';
import { getFreedrawShape } from './utils/shape';

export type StrokeCap = 'round' | 'square';

export interface GenericFreedrawElement extends GenericPolyElement {
  strokeColor: string;
  strokeWidth: number;
  strokeCap: StrokeCap;
}

export interface FreedrawElement extends GenericFreedrawElement {
  type: 'freedraw';
}

export interface FreedrawState<T extends GenericFreedrawElement> {
  newStrokeColor: string;
  newStrokeWidth: number;
  booleanMode: BooleanElementGroupType;
  newStrokeCap: StrokeCap;
  lastPoint: Point | null;
  lastDrawnElement: T | null;
}

const freedrawState: FreedrawState<GenericFreedrawElement> = {
  newStrokeColor: '#3c87f7',
  newStrokeWidth: 16,
  booleanMode: 'union',
  newStrokeCap: 'round',
  lastPoint: null,
  lastDrawnElement: null,
};

const freedrawActions = {
  activateBrush: () => {
    general.actions.activate(plugin.name, true);
    freedrawState.booleanMode = 'union';
  },
  activateEraser: () => {
    general.actions.activate(plugin.name, true);
    freedrawState.booleanMode = 'subtract';
  },
  setStrokeColor: (color: string) => {
    freedrawState.newStrokeColor = hexToRgbString(color, 0.8);
  },
  setStrokeWidth: (width: number) => {
    freedrawState.newStrokeWidth = width;
  },
  setStrokeCap: (cap: StrokeCap) => {
    freedrawState.newStrokeCap = cap;
  },
  updateColorForElement: (id: string, color: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(id) as GenericPolyElement;

    if (element?.type)
      updateElement<GenericPolyElement>(elements, element, {
        strokeColor: color,
        ...reversionElement(element, ['canvas']),
      });

    renderStaticScene();
  },
};

const plugin: Plugin<
  GenericFreedrawElement,
  typeof freedrawActions,
  typeof freedrawState
> = {
  name: 'freedraw',
  cursor: 'default',
  actions: freedrawActions,
  state: freedrawState,
  events: {
    ...createFreedrawEvents<GenericFreedrawElement>({
      toolName: 'freedraw',
      elementType: 'freedraw',
      state: freedrawState,
    }),
    blockTouchPanning: true,
  },
  render: {
    // By default the dimensions are calculated from the points. But the points are the center from the
    // freedraw path. So that the line is not cut at the edges, we need to add additional padding.
    padding: (element) => {
      if (element.strokeCap === 'square') {
        return element.strokeWidth * Math.SQRT2;
      }
      return element.strokeWidth;
    },
    // General render functions. This generates a svg path from the points and draws it on the canvas.
    // Keep in mind that positioning, rotating and so on is done by internal logic. This logic
    // draws the element on a new canvas, which is then drawn on the main canvas.
    draw: ({ config, context, element, runtimeState }) => {
      if (!runtimeState.newElement && !element.points.length) return;

      if (element.renderedById != null) return;

      context.save();

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      context.strokeStyle = element.strokeColor;
      context.lineWidth = element.strokeWidth;
      context.lineCap = element.strokeCap ?? 'round';

      const padding =
        element.strokeCap === 'square'
          ? element.strokeWidth * Math.SQRT2
          : element.strokeWidth;
      const offset = padding / 2;

      const path = getStrokePath(element.points, element.bezierHandles, offset);
      context.stroke(path);

      context.restore();
    },
    // The initial click sets the x and y position of the element. This is the left upper corner of the
    // canvas element. Drawing to the right and down will only increase the width and height of the element,
    // which works fine. Drawing to the left or up will change the x and y position of the element. This
    // is why we need to translate these cases manually.
    translate: (context, element, scale) => {
      const [x1, y1] = getFreeDrawElementAbsoluteCoords(element);

      const canvasOffsetX =
        element.x > x1 ? distance(element.x, x1) * scale : 0;
      const canvasOffsetY =
        element.y > y1 ? distance(element.y, y1) * scale : 0;

      context.translate(canvasOffsetX, canvasOffsetY);

      return { x: canvasOffsetX, y: canvasOffsetY };
    },
  },
  selection: { ...poly.selection, renderTransformHandlesForNewElement: false },
  collision: {
    // This method should return the bounds of the element. In general it means the coordinates of the
    // 4 corners of the element and the center. This should consider the angle of the element.
    // However in some cases like to selection box of the element, the bounds without angle are needed,
    // because of the underlying rendering logic.
    bounds: (element) => {
      const [minX, minY, maxX, maxY, centerX, centerY] =
        poly.collision.bounds(element);

      const padding =
        element.strokeCap === 'square'
          ? (element.strokeWidth * Math.SQRT2) / 2
          : element.strokeWidth / 2;

      return [
        minX - padding,
        minY - padding,
        maxX + padding,
        maxY + padding,
        centerX,
        centerY,
      ];
    },
    // The shape is used for collision detection on mouse events. This method should return the shape
    // of the element. In this case it is a polyline. The polyline is calculated from the points of
    // the element. This functions gets called multiple times, if the shape has changed (e.g. transformation).
    calculateShape: (element) => getFreedrawShape(element),
    // Detects if a point is inside the shape of the element. This is also used for mouse events. If the
    // shape has no inside, this method should return false.
    isPointInShape: poly.collision.isPointInShape,
    // Detects if a point is exactly on the shape of the element. This is also used for mouse events. In
    // general this should be implemented every time, because every element has a border. However for
    // rectangle like objects, the border is the shape itself. In this case the method could return false.
    // The tolerance is used to increase the area where the point is considered on the shape.
    isPointOnShape: poly.collision.isPointInShape,
    onlyCountDirectElementHitAfterSelection: true,
  },
  transformation: { ...poly.transformation },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<FreedrawElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as FreedrawElement;

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const freedraw = plugin;
